import * as styles from "./Section.css";

import { clsx } from "clsx";
import type { JSX, ReactNode } from "react";

import { FieldGroup } from "@frontend/components/atoms/Field";
import { Fieldset, FieldsetLegend } from "@frontend/components/atoms/Fieldset";
import type { IconName } from "@frontend/components/atoms/Icon";

export type SectionProps = {
  icon?: IconName;
  legend: ReactNode;
  /** What the legend carries on its right, a button or a popover trigger. */
  action?: ReactNode;
  className?: string;
  children: ReactNode;
};

export type SplitSectionProps = {
  icon?: IconName;
  legend: ReactNode;
  left: ReactNode;
  right: ReactNode;
};

/**
 * The frame of a section without its label column: a legend, then whatever
 * the section lays out on its own, a list of cards or a table of prices.
 */
export function SectionFrame({
  icon,
  legend,
  action,
  className,
  children,
}: SectionProps): JSX.Element {
  return (
    <Fieldset variant="section" className={className}>
      <FieldsetLegend icon={icon} action={action}>
        {legend}
      </FieldsetLegend>

      {children}
    </Fieldset>
  );
}

/**
 * One block of the form: a legend, then fields whose labels line up in a
 * column of their own, which is how the legacy page reads.
 */
export function Section({
  icon,
  legend,
  className,
  children,
}: SectionProps): JSX.Element {
  return (
    <Fieldset variant="section" className={clsx(styles.section, className)}>
      <FieldsetLegend icon={icon}>{legend}</FieldsetLegend>

      <FieldGroup orientation="horizontal" labelAlign="right">
        {children}
      </FieldGroup>
    </Fieldset>
  );
}

/** A section the legacy page lays out in two halves. */
export function SplitSection({
  icon,
  legend,
  left,
  right,
}: SplitSectionProps): JSX.Element {
  return (
    <Fieldset variant="section" className={styles.section}>
      <FieldsetLegend icon={icon}>{legend}</FieldsetLegend>

      <div className={styles.split}>
        <FieldGroup orientation="horizontal" labelAlign="right">
          {left}
        </FieldGroup>

        <FieldGroup orientation="horizontal" labelAlign="right">
          {right}
        </FieldGroup>
      </div>
    </Fieldset>
  );
}
